import * as React from 'react';
import {useState} from 'react';
import {InfoBox, Loader} from '@flexe/ui-components';
import ItemMasterService from '../ItemMasterService';
import {
  HazmatClassification,
  HazmatViewData,
  InventoryConfiguration,
  InventoryInfo,
  InventorySummary
} from './ItemMasterInterfaces';
import {InventorySummaryTable} from './InventorySummaryTable';
import UomTile from './UomTile';
import SkuDetails from './SkuDetails';
import {ItemMasterDetailHeader} from './ItemMasterDetailHeader';
import {ItemMasterEdit, Packaging, PACKAGING_ORDER} from './ItemMasterEdit';

export interface SerialNumberProgram {
  name: string;
  description: string;
}

export interface InventoryGroups {
  description: string;
  id: number;
  companyId: number;
}

export interface UpdatedItemDetails {
  updatedInventoryInfo: InventoryInfo;
  updatedInventoryConfiguration: InventoryConfiguration;
  updatedHazmatViewdata: HazmatViewData;
  updatedHazmatClassification: HazmatClassification;
}

export interface ItemMasterDetailProps {
  shipperCompanyId: number;
  allowSkuEdit: boolean;
  authenticityToken?: string;
  isShipper: boolean;
  inventoryInfo: InventoryInfo;
  inventorySummary: InventorySummary;
  inventoryGroups: InventoryGroups[];
  altIdPresence: boolean;
  serial_number_program_enabled: boolean;
  serial_number_program: string;
  serial_number_programs: SerialNumberProgram[];
  inventory_configuration: InventoryConfiguration;
  hazmat_enabled: boolean;
  hazmat_viewdata: HazmatViewData;
  hazmat_classification: HazmatClassification;
  enable_item_storage_class: boolean;
  vas_receive_workflow: boolean;
  inventory_control_user: boolean;
  fai_receive_workflow: boolean;
}

export interface ToastProps {
  visible: boolean;
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
}

const ItemMasterDetail: React.FC<ItemMasterDetailProps> = (props) => {
  const itemMasterService = new ItemMasterService(props.authenticityToken);
  const [loading, setLoading] = useState<boolean>(false);
  const [mode, setMode] = useState<'detail' | 'edit'>('detail');
  const [infoState, setInfoState] = useState<InventoryInfo>(props.inventoryInfo);
  const [configState, setConfigState] = useState<InventoryConfiguration>(props.inventory_configuration);
  const [hazmatClassificationState, setHazmatClassificationState] = useState<HazmatClassification>(
    props.hazmat_classification
  );
  const [hazmatViewDataState, setHazmatViewDataState] = useState<HazmatViewData>(props.hazmat_viewdata);
  const [toast, setToast] = useState<ToastProps>({visible: false, type: 'success', message: ''});

  const cancelEdit = () => {
    setMode('detail');
    setToast({visible: true, type: 'info', message: 'Item Master Detail Update Cancelled'});
  };
  const onSaved = (updatedItemDetails: UpdatedItemDetails) => {
    setInfoState(updatedItemDetails.updatedInventoryInfo);
    setConfigState(updatedItemDetails.updatedInventoryConfiguration);
    setHazmatViewDataState(updatedItemDetails.updatedHazmatViewdata);
    setHazmatClassificationState(updatedItemDetails.updatedHazmatClassification);
    setToast({...toast, visible: true, type: 'success', message: 'Item Master Detail Update Successful'});
    setMode('detail');
  };

  if (mode === 'edit') {
    return (
      <ItemMasterEdit
        {...props}
        inventoryInfo={infoState}
        inventory_configuration={configState}
        hazmat_viewdata={hazmatViewDataState}
        hazmat_classification={hazmatClassificationState}
        onSaved={onSaved}
        cancelEdit={cancelEdit}
      />
    );
  }

  return (
    <>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div id="item-master-detail">
          {toast.visible && (
            <div>
              <InfoBox info={toast.message} infoType={toast.type} />
            </div>
          )}
          <div className="container-fluid detail-header">
            <ItemMasterDetailHeader isShipper={props.isShipper} />

            <div className="row">
              <h2 className="col-sm-8 space-below">SKU: {infoState.sku}</h2>
              {(props.isShipper || props.inventory_control_user) && (
                <div className="col-sm-4 action-buttons text-right">
                  <a href="#" className="btn edit-btn" onClick={() => setMode('edit')}>
                    <i className="fa fa-pencil"></i> Edit
                  </a>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-sm-12">
                <b>Description:</b> {infoState.description}
              </div>
            </div>
          </div>

          <div className="detail-body">
            <div className="container-fluid detail-inset">
              <div className="row">
                <div className="col-sm-12">
                  <InventorySummaryTable summary={props.inventorySummary} />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6">
                  {infoState.properties
                    .sort((a, b) => {
                      const oa = PACKAGING_ORDER[a.packaging as Packaging];
                      const ob = PACKAGING_ORDER[b.packaging as Packaging];
                      return oa - ob;
                    })
                    .map((property, i) => (
                      <UomTile
                        key={i}
                        isShipper={props.isShipper}
                        inventoryControlUser={props.inventory_control_user}
                        faiReceiveWorkflow={props.fai_receive_workflow}
                        inventoryProp={property}
                        inventoryInfo={infoState}
                      />
                    ))}
                </div>
                <div className="col-sm-6">
                  <SkuDetails
                    inventoryInfo={infoState}
                    altIdPresence={props.altIdPresence}
                    serial_number_program_enabled={props.serial_number_program_enabled}
                    serial_number_program={infoState.serial_number_program}
                    inventory_configuration={configState}
                    hazmat_enabled={props.hazmat_enabled}
                    hazmat_viewdata={hazmatViewDataState}
                    hazmat_classification={hazmatClassificationState}
                    enable_item_storage_class={props.enable_item_storage_class}
                    vas_receive_workflow={props.vas_receive_workflow}
                    inventory_control_user={props.inventory_control_user}
                    fai_receive_workflow={props.fai_receive_workflow}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ItemMasterDetail;
